/* eslint-disable react/prop-types */
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "contexts/AuthContext";
import { API_BASE } from "config/api";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Chip from "@mui/material/Chip";
import Avatar from "@mui/material/Avatar";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import { useTheme } from "@mui/material/styles";
import Swal from "sweetalert2";

import StatTile from "shell/StatTile";

/**
 * Shared dashboard for department-heads and ministry-heads.
 * Prop: kind = "department" | "ministry"
 */
export default function HeadDashboard({ kind = "department" }) {
  const theme = useTheme();
  const { user, church } = useAuth();
  const token = localStorage.getItem("authToken");
  const churchId = church?.id;
  const label = kind === "ministry" ? "Ministry" : "Department";

  const [group, setGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [duesReport, setDuesReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState("");

  const headers = useMemo(
    () => ({ Accept: "application/json", Authorization: `Bearer ${token}` }),
    [token]
  );

  useEffect(() => {
    if (!user?.member_id || !churchId || !token) return setLoading(false);
    const path = kind === "ministry" ? "ministries" : "departments";
    fetch(`${API_BASE}/${path}/church/${churchId}`, { headers })
      .then((r) => r.json())
      .then((j) => {
        const list = Array.isArray(j) ? j : j?.data || [];
        setGroup(list.find((g) => Number(g.head_member_id) === Number(user.member_id)) || null);
      })
      .catch((e) => console.error("group lookup:", e));
  }, [kind, churchId, user?.member_id, token, headers]);

  useEffect(() => {
    if (!group || !churchId) return setLoading(false);
    setLoading(true);
    const path = kind === "ministry" ? "ministries" : "department";
    const duesPath = kind === "ministry" ? "ministry/dues" : "department/dues";
    Promise.all([
      fetch(`${API_BASE}/${path}/members/${churchId}/${group.id}`, { headers })
        .then((r) => r.json())
        .then((j) => (Array.isArray(j) ? j : j?.data || [])),
      fetch(`${API_BASE}/${path}/attendance/${churchId}/${group.id}`, { headers })
        .then((r) => r.json())
        .then((j) => (Array.isArray(j) ? j : j?.data || j?.attendance || [])),
      fetch(`${API_BASE}/${duesPath}/${churchId}/${group.id}`, { headers })
        .then((r) => r.json())
        .catch(() => null),
    ])
      .then(([m, a, dues]) => {
        setMembers(m);
        setAttendance(a);
        setDuesReport(dues);
      })
      .catch((e) => console.error("head data fetch:", e))
      .finally(() => setLoading(false));
  }, [group?.id, churchId, kind, headers]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return members;
    return members.filter((m) =>
      `${m.first_name || ""} ${m.surname || ""} ${m.memberID || ""} ${m.phone || ""}`
        .toLowerCase()
        .includes(q)
    );
  }, [members, search]);

  const todayCount = useMemo(
    () =>
      attendance.filter(
        (a) =>
          new Date(a.checked_in_at || a.check_time).toDateString() === new Date().toDateString()
      ).length,
    [attendance]
  );

  const checkIn = async (member) => {
    try {
      const body = { member_id: member.id, church_id: churchId, method: "manual" };
      if (kind === "ministry") body.ministry_id = group.id;
      else body.department_id = group.id;
      const res = await fetch(`${API_BASE}/attendance`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error((await res.json())?.message || "Check-in failed");
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "success",
        title: `${member.first_name} ${member.surname} checked in`,
        showConfirmButton: false,
        timer: 2000,
      });
      // refresh attendance
      const path = kind === "ministry" ? "ministries" : "department";
      const a = await fetch(`${API_BASE}/${path}/attendance/${churchId}/${group.id}`, { headers })
        .then((r) => r.json())
        .then((j) => (Array.isArray(j) ? j : j?.data || j?.attendance || []));
      setAttendance(a);
    } catch (e) {
      Swal.fire({ title: "Error", text: e.message, icon: "error" });
    }
  };

  if (!user?.member_id) {
    return <Empty title="No member profile linked to this user." />;
  }
  if (loading && !group) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 8 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!group) {
    return (
      <Card sx={{ maxWidth: 540, mx: "auto", mt: 6, overflow: "hidden" }}>
        <Box
          sx={{
            p: 3,
            background: `linear-gradient(135deg, ${theme.palette.teal[600]}, ${theme.palette.teal[400]})`,
            color: "white",
          }}
        >
          <Icon sx={{ color: "white", fontSize: 40 }}>info</Icon>
          <Typography variant="h4" sx={{ color: "white", mt: 1 }}>
            No {label.toLowerCase()} assigned yet
          </Typography>
        </Box>
        <Box sx={{ p: 3 }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {`Your account has the ${label} Head role, but no ${label.toLowerCase()} currently lists you as its head.`}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {`Ask an administrator to open Users → edit your account, choose "${label} to lead", and pick the ${label.toLowerCase()} you should manage.`}
          </Typography>
        </Box>
      </Card>
    );
  }

  return (
    <Box>
      {/* Hero */}
      <Card
        sx={{
          mb: 3,
          p: { xs: 2.5, md: 3.5 },
          color: "white",
          background:
            kind === "ministry"
              ? "linear-gradient(135deg,#7B1FA2,#BA68C8)"
              : `linear-gradient(135deg, ${theme.palette.teal[600]}, ${theme.palette.teal[400]})`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: 2,
              bgcolor: "rgba(255,255,255,0.18)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon sx={{ color: "white", fontSize: 28 }}>
              {kind === "ministry" ? "diversity_3" : "groups"}
            </Icon>
          </Box>
          <Box>
            <Typography variant="overline" sx={{ color: "white", opacity: 0.9 }}>
              {label} Head
            </Typography>
            <Typography variant="h2" sx={{ color: "white" }}>
              {group.name}
            </Typography>
          </Box>
        </Box>
      </Card>

      {/* Stats */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            icon="group"
            label={`Members in ${label}`}
            value={members.length}
            tone="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            icon="how_to_reg"
            label="Total attendance"
            value={attendance.length}
            tone="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile icon="today" label="Checked in today" value={todayCount} tone="warning" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            icon="trending_up"
            label="Today's rate"
            value={members.length ? `${Math.round((todayCount / members.length) * 100)}%` : "—"}
            tone="purple"
          />
        </Grid>
      </Grid>

      <Card>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            px: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
            "& .MuiTab-root": { textTransform: "none", fontWeight: 600 },
          }}
        >
          <Tab icon={<Icon fontSize="small">group</Icon>} iconPosition="start" label="Members" />
          <Tab
            icon={<Icon fontSize="small">how_to_reg</Icon>}
            iconPosition="start"
            label="Attendance"
          />
          <Tab icon={<Icon fontSize="small">summarize</Icon>} iconPosition="start" label="Report" />
        </Tabs>

        <Box sx={{ p: 2 }}>
          {tab === 0 && (
            <>
              <TextField
                placeholder="Search members…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                fullWidth
                size="small"
                sx={{ mb: 2 }}
              />
              <Grid container spacing={2}>
                {filtered.length === 0 ? (
                  <Grid item xs={12}>
                    <Empty title="No members match." />
                  </Grid>
                ) : (
                  filtered.map((m) => (
                    <Grid item xs={12} sm={6} md={4} key={m.id}>
                      <Card sx={{ p: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Avatar
                            src={
                              m.photo
                                ? `${API_BASE.replace(/\/api$/, "")}/storage/${m.photo}`
                                : undefined
                            }
                            sx={{
                              width: 44,
                              height: 44,
                              bgcolor: theme.palette.teal[100],
                              color: theme.palette.teal[700],
                              fontWeight: 700,
                            }}
                          >
                            {(m.first_name?.[0] || "?").toUpperCase()}
                          </Avatar>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="subtitle2" noWrap>
                              {m.first_name} {m.surname}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {m.memberID || m.phone}
                            </Typography>
                          </Box>
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            onClick={() => checkIn(m)}
                            startIcon={<Icon>check</Icon>}
                          >
                            Check in
                          </Button>
                        </Box>
                      </Card>
                    </Grid>
                  ))
                )}
              </Grid>
            </>
          )}
          {tab === 1 &&
            (attendance.length === 0 ? (
              <Empty title="No attendance records yet." />
            ) : (
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Member</TableCell>
                      <TableCell>When</TableCell>
                      <TableCell>Method</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {attendance.map((a) => (
                      <TableRow key={a.id || `${a.member_id}-${a.checked_in_at}`} hover>
                        <TableCell>
                          {a.member
                            ? `${a.member.first_name} ${a.member.surname}`
                            : a.member_name || "—"}
                        </TableCell>
                        <TableCell>
                          {new Date(a.checked_in_at || a.check_time).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={a.method}
                            color={a.method === "biometric" ? "success" : "default"}
                            sx={{ textTransform: "capitalize" }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            ))}
          {tab === 2 && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <StatTile
                    icon="payments"
                    label={`Total ${label.toLowerCase()} dues & contributions`}
                    value={`GHS ${Number(duesReport?.meta?.total || 0).toLocaleString()}`}
                    tone="success"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <StatTile
                    icon="receipt_long"
                    label="Payments recorded"
                    value={duesReport?.meta?.count || 0}
                    tone="info"
                  />
                </Grid>
              </Grid>

              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                Breakdown by type
              </Typography>
              {!duesReport?.meta?.by_type || Object.keys(duesReport.meta.by_type).length === 0 ? (
                <Empty title="No dues or contributions recorded for this group yet." />
              ) : (
                <Box sx={{ overflowX: "auto", mb: 3 }}>
                  <Table size="small">
                    <TableBody>
                      {Object.entries(duesReport.meta.by_type).map(([type, amount]) => (
                        <TableRow key={type}>
                          <TableCell sx={{ textTransform: "capitalize" }}>{type}</TableCell>
                          <TableCell align="right">GHS {Number(amount).toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}

              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                Recent payments
              </Typography>
              {!duesReport?.data || duesReport.data.length === 0 ? (
                <Empty title="No payments recorded yet." />
              ) : (
                <Box sx={{ overflowX: "auto" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Member</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell align="right">Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {duesReport.data.slice(0, 10).map((p) => (
                        <TableRow key={p.id} hover>
                          <TableCell>
                            {p.member ? `${p.member.first_name} ${p.member.surname}` : "—"}
                          </TableCell>
                          <TableCell sx={{ textTransform: "capitalize" }}>{p.type}</TableCell>
                          <TableCell>{new Date(p.payment_date).toLocaleDateString()}</TableCell>
                          <TableCell align="right">
                            GHS {Number(p.amount).toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Card>
    </Box>
  );
}

function Empty({ title }) {
  return (
    <Box sx={{ textAlign: "center", py: 6 }}>
      <Icon sx={{ fontSize: 40, color: "text.disabled" }}>inbox</Icon>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {title}
      </Typography>
    </Box>
  );
}
